import 'leaflet/dist/leaflet.css'

import React, { useState, useEffect, useRef, useMemo, useCallback, memo } from 'react'
import { toJS } from 'mobx'
import L from 'leaflet'
import JSZip from 'jszip'
import { saveAs } from 'file-saver'
import { Map, TileLayer, Rectangle, CircleMarker, Tooltip, Marker, LayersControl } from 'react-leaflet'

import Page from '../page'
import Loading from '../../components/loading'
import { NavInExperience } from '../../components/nav'
import * as api from '../../lib/api'
import { conf } from '../../stores'

// ─── Constantes ───────────────────────────────────────────────────────────────

const MAPBOX_TOKEN = conf.MAPBOX_TOKEN
const MAPBOX_BASE = 'https://api.mapbox.com/styles/v1'
const CONCURRENCY = 20

const MAPBOX_STYLES = [
  { value: 'mapbox/satellite-v9', label: 'Satellite' },
  { value: 'mapbox/satellite-streets-v11', label: 'Satellite + rues' },
  { value: 'mapbox/streets-v11', label: 'Plan (rues)' },
  { value: 'mapbox/outdoors-v11', label: 'Outdoors' },
  { value: 'mapbox/light-v10', label: 'Light' },
  { value: 'mapbox/dark-v10', label: 'Dark' }
]

// Préréglages de sources XYZ tierces
const TILE_PRESETS = [
  { label: 'OpenStreetMap', url: 'https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png' },
  { label: 'CartoDB Clair', url: 'https://{s}.basemaps.cartocdn.com/light_all/{z}/{x}/{y}.png' },
  { label: 'CartoDB Sombre', url: 'https://{s}.basemaps.cartocdn.com/dark_all/{z}/{x}/{y}.png' },
  { label: 'OpenTopoMap', url: 'https://{s}.tile.opentopomap.org/{z}/{x}/{y}.png' },
  { label: 'Stadia (pastel)', url: 'https://tiles.stadiamaps.com/tiles/alidade_smooth/{z}/{x}/{y}.png' }
]

// Couches affichées dans la carte de prévisualisation (ordre = ordre dans le sélecteur)
const PREVIEW_LAYERS = [
  { value: 'mapbox/satellite-streets-v11', name: 'Satellite + rues' },
  { value: 'mapbox/satellite-v9', name: 'Satellite' },
  { value: 'mapbox/outdoors-v11', name: 'Outdoors' },
  { value: 'mapbox/streets-v11', name: 'Plan' },
  { value: 'mapbox/light-v10', name: 'Light' },
  { value: 'mapbox/dark-v10', name: 'Dark' }
]

// Tailles de repli (octets) utilisées si l'échantillonnage Mapbox échoue
const FALLBACK_TILE_BYTES = {
  'mapbox/satellite-v9': 30_000,
  'mapbox/satellite-streets-v11': 35_000,
  'mapbox/streets-v11': 15_000,
  'mapbox/outdoors-v11': 20_000,
  'mapbox/light-v10': 12_000,
  'mapbox/dark-v10': 12_000
}

const cornerIcon = new L.DivIcon({
  html:
    '<div style="width:12px;height:12px;background:white;border:2.5px solid orange;border-radius:3px;box-shadow:0 1px 4px rgba(0,0,0,.45);cursor:nwse-resize"></div>',
  className: '',
  iconSize: [12, 12],
  iconAnchor: [6, 6]
})

// ─── Utilitaires ──────────────────────────────────────────────────────────────

function deg2num(zoom, lat, lon) {
  const latRad = (lat * Math.PI) / 180
  const n = Math.pow(2, zoom)
  const x = Math.floor(((lon + 180) / 360) * n)
  const y = Math.floor(((1 - Math.log(Math.tan(latRad) + 1 / Math.cos(latRad)) / Math.PI) / 2) * n)
  return [x, y]
}

function countTiles(zMin, zMax, north, west, south, east) {
  let total = 0
  for (let z = zMin; z <= zMax; z++) {
    const [x1, y1] = deg2num(z, north, west)
    const [x2, y2] = deg2num(z, south, east)
    total += (Math.abs(x2 - x1) + 1) * (Math.abs(y2 - y1) + 1)
  }
  return total
}

function suggestZoomLevels(north, south, east, west) {
  const midLat = (north + south) / 2
  const kmLat = Math.abs(north - south) * 111
  const kmLon = Math.abs(east - west) * 111 * Math.cos((midLat * Math.PI) / 180)
  const km = Math.max(kmLat, kmLon)
  if (km > 200) return { zoomMin: 8, zoomMax: 13 }
  if (km > 50) return { zoomMin: 10, zoomMax: 15 }
  if (km > 10) return { zoomMin: 12, zoomMax: 16 }
  if (km > 2) return { zoomMin: 14, zoomMax: 17 }
  return { zoomMin: 15, zoomMax: 18 }
}

function computeBbox(lat1, lon1, lat2, lon2) {
  return {
    north: Math.max(lat1, lat2),
    south: Math.min(lat1, lat2),
    east: Math.max(lon1, lon2),
    west: Math.min(lon1, lon2)
  }
}

function formatBytes(bytes) {
  const mb = bytes / (1024 * 1024)
  if (mb >= 100) return `~${Math.round(mb)} Mo`
  if (mb >= 1) return `~${mb.toFixed(1)} Mo`
  const kb = bytes / 1024
  return kb >= 1 ? `~${Math.round(kb)} Ko` : '<1 Ko'
}

function formatEta(seconds) {
  if (seconds < 60) return `${Math.ceil(seconds)}s`
  return `${Math.ceil(seconds / 60)} min`
}

function formatSpeed(bytesPerSec) {
  const kbps = bytesPerSec / 1024
  if (kbps >= 1024) return `${(kbps / 1024).toFixed(1)} Mo/s`
  return `${Math.round(kbps)} Ko/s`
}

/**
 * Construit une URL de tuile à partir des paramètres courants.
 * Retourne une fonction (zoom, x, y) => string.
 */
function buildTileUrl(params) {
  if (params.sourceMode !== 'custom') {
    return (z, x, y) => `${MAPBOX_BASE}/${params.style}/tiles/${z}/${x}/${y}?access_token=${MAPBOX_TOKEN}`
  }
  const subs = 'abc'
  return (z, x, y) => {
    const s = subs[Math.floor(Math.random() * subs.length)]
    let url = (params.customUrl || '')
      .replace('{z}', z)
      .replace('{x}', x)
      .replace('{y}', y)
      .replace('{s}', s)
    if (params.customToken) {
      url += (url.includes('?') ? '&' : '?') + `access_token=${params.customToken}`
    }
    return url
  }
}

/**
 * Construit l'URL à passer à Leaflet (garde les variables natives {z}/{x}/{y}).
 * Remplace seulement {s} par la notation Leaflet {s} (passé via subdomains).
 */
function leafletUrl(params) {
  if (params.sourceMode !== 'custom') return null
  let url = params.customUrl || ''
  if (params.customToken) {
    url += (url.includes('?') ? '&' : '?') + `access_token=${params.customToken}`
  }
  return url
}

// ─── Téléchargement des tuiles ────────────────────────────────────────────────

/**
 * Échantillonne N tuiles au centre du cadre pour mesurer leur taille réelle.
 * Retourne la taille moyenne en octets.
 */
async function sampleTileBytes(urlBuilder, fallbackBytes, zoom, bbox, sampleCount = 4) {
  const [x1, y1] = deg2num(zoom, bbox.north, bbox.west)
  const [x2, y2] = deg2num(zoom, bbox.south, bbox.east)
  const xMin = Math.min(x1, x2),
    xMax = Math.max(x1, x2)
  const yMin = Math.min(y1, y2),
    yMax = Math.max(y1, y2)
  if ((xMax - xMin + 1) * (yMax - yMin + 1) === 0) return fallbackBytes

  const xMid = Math.floor((xMin + xMax) / 2)
  const yMid = Math.floor((yMin + yMax) / 2)
  const half = Math.ceil(Math.sqrt(sampleCount))
  const picked = []
  for (let dx = -half; dx <= half && picked.length < sampleCount; dx++) {
    for (let dy = -half; dy <= half && picked.length < sampleCount; dy++) {
      const x = xMid + dx,
        y = yMid + dy
      if (x >= xMin && x <= xMax && y >= yMin && y <= yMax) picked.push([x, y])
    }
  }
  if (picked.length === 0) return fallbackBytes

  const sizes = await Promise.all(
    picked.map(async ([x, y]) => {
      try {
        const res = await fetch(urlBuilder(zoom, x, y))
        if (!res.ok) return null
        return (await res.arrayBuffer()).byteLength
      } catch {
        return null
      }
    })
  )

  const valid = sizes.filter(s => s !== null)
  return valid.length > 0 ? valid.reduce((a, b) => a + b, 0) / valid.length : fallbackBytes
}

/**
 * Télécharge une tuile avec retry exponentiel.
 * Retourne null sur échec définitif (pour ne pas bloquer le pool).
 */
async function fetchTile(urlBuilder, zoom, x, y, signal, retries = 2) {
  for (let attempt = 0; attempt <= retries; attempt++) {
    try {
      const res = await fetch(urlBuilder(zoom, x, y), { signal })
      if (!res.ok) throw new Error(`HTTP ${res.status}`)
      return { path: `${zoom}/${x}/${y}`, buffer: await res.arrayBuffer() }
    } catch (e) {
      if (e.name === 'AbortError') return null
      if (attempt === retries) return null
      await new Promise(r => setTimeout(r, 300 * (attempt + 1)))
    }
  }
}

/**
 * Pool de N workers concurrents.
 * onProgress(done, failed, zoom, bytesDownloaded) appelé après chaque tuile.
 * Retourne { zip, failed: [{z,x,y}] }
 */
async function downloadPool(tileList, urlBuilder, format, concurrency, signal, onProgress) {
  const zip = new JSZip()
  const failed = []
  let nextIdx = 0
  let done = 0
  let totalBytes = 0

  async function worker() {
    while (nextIdx < tileList.length) {
      if (signal && signal.aborted) break
      const [z, x, y] = tileList[nextIdx++]
      const result = await fetchTile(urlBuilder, z, x, y, signal)
      if (result) {
        zip.file(`${result.path}.${format}`, result.buffer, { compression: 'STORE' })
        totalBytes += result.buffer.byteLength
      } else {
        if (!signal || !signal.aborted) failed.push({ z, x, y })
      }
      onProgress(++done, failed.length, z, totalBytes)
    }
  }

  await Promise.all(Array.from({ length: Math.min(concurrency, tileList.length) }, worker))
  return { zip, failed }
}

// ─── Carte de prévisualisation ────────────────────────────────────────────────

const PreviewMap = memo(({ points, bbox, centerKey, onBboxChange, customLayerUrl, selectedStyle }) => {
  const mapRef = useRef(null)
  const bboxRef = useRef(bbox)
  useEffect(() => {
    bboxRef.current = bbox
  }, [bbox])

  useEffect(() => {
    if (!mapRef.current || !bbox) return
    mapRef.current.leafletElement.fitBounds(
      [
        [bbox.south, bbox.west],
        [bbox.north, bbox.east]
      ],
      { padding: [30, 30] }
    )
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [centerKey])

  const onCornerDragEnd = useCallback(
    (corner, e) => {
      const { lat, lng } = e.target.getLatLng()
      const b = { ...bboxRef.current }
      if (corner[0] === 'n') b.north = lat
      else b.south = lat
      if (corner[1] === 'e') b.east = lng
      else b.west = lng
      onBboxChange(b)
    },
    [onBboxChange]
  )

  const corners = useMemo(
    () =>
      bbox
        ? [
            { id: 'ne', pos: [bbox.north, bbox.east] },
            { id: 'nw', pos: [bbox.north, bbox.west] },
            { id: 'se', pos: [bbox.south, bbox.east] },
            { id: 'sw', pos: [bbox.south, bbox.west] }
          ]
        : [],
    [bbox]
  )

  return (
    <Map ref={mapRef} center={[46.5, 2.5]} zoom={5} style={{ height: 480, borderRadius: 4, border: '1px solid #ddd' }}>
      <LayersControl position="topright">
        {PREVIEW_LAYERS.map(layer => (
          <LayersControl.BaseLayer key={layer.value} name={layer.name} checked={!customLayerUrl && selectedStyle === layer.value}>
            <TileLayer
              url={`${MAPBOX_BASE}/${layer.value}/tiles/512/{z}/{x}/{y}{r}?access_token=${MAPBOX_TOKEN}`}
              attribution="© Mapbox"
              tileSize={512}
              zoomOffset={-1}
            />
          </LayersControl.BaseLayer>
        ))}
        {customLayerUrl && (
          <LayersControl.BaseLayer name="Source personnalisée" checked>
            <TileLayer url={customLayerUrl} attribution="© Source personnalisée" subdomains="abc" />
          </LayersControl.BaseLayer>
        )}
      </LayersControl>

      {bbox && (
        <>
          <Rectangle
            bounds={[
              [bbox.south, bbox.west],
              [bbox.north, bbox.east]
            ]}
            color="orange"
            weight={2}
            fillColor="orange"
            fillOpacity={0.08}
          />
          {corners.map(({ id, pos }) => (
            <Marker key={id} position={pos} draggable icon={cornerIcon} onDragend={e => onCornerDragEnd(id, e)} />
          ))}
        </>
      )}

      {points.map((loc, i) => (
        <CircleMarker key={i} center={loc} radius={6} color="#c0392b" fillColor="#e74c3c" fillOpacity={0.85} weight={2}>
          {loc.name && (
            <Tooltip direction="top" offset={[0, -8]}>
              {loc.name}
            </Tooltip>
          )}
        </CircleMarker>
      ))}
    </Map>
  )
})

// ─── Page principale ──────────────────────────────────────────────────────────

export const CartExport = props => {
  const { experienceUid } = props.match.params

  const [loading, setLoading] = useState(true)
  const [mapPoints, setMapPoints] = useState([])
  const [params, setParams] = useState({
    zipName: 'carto',
    zoomMin: 14,
    zoomMax: 18,
    lat1: '',
    lon1: '',
    lat2: '',
    lon2: '',
    sourceMode: 'mapbox',
    style: 'mapbox/satellite-v9',
    customUrl: '',
    customToken: '',
    format: 'png'
  })
  const [centerKey, setCenterKey] = useState(0)
  const [status, setStatus] = useState('idle')
  const [progress, setProgress] = useState({ done: 0, total: 0, zoom: null, failed: 0, bytes: 0 })
  const [zipBlob, setZipBlob] = useState(null)
  const [error, setError] = useState(null)
  const [estimate, setEstimate] = useState(null)

  const genStartRef = useRef(null)
  const abortCtrlRef = useRef(null)

  // ── Chargement initial ─────────────────────────────────────────────────────
  useEffect(() => {
    const load = async () => {
      setLoading(true)
      try {
        const [experience, tracks] = await Promise.all([
          api.experiences.get(experienceUid),
          api.tracks.getFrom('experiences', experienceUid)
        ])

        const rawPoints = []
        for (const track of tracks || []) {
          const pts = await api.points.getFrom('tracks', track._id)
          for (const p of pts || []) {
            if (p.location && p.location.length === 2) {
              const loc = toJS(p.location)
              loc.name = p.name || ''
              rawPoints.push(loc)
            }
          }
        }
        setMapPoints(rawPoints)

        const updates = {}
        if (experience?.name) {
          updates.zipName = experience.name.replace(/[^a-zA-Z0-9_-]/g, '_')
        }
        if (rawPoints.length >= 2) {
          const lats = rawPoints.map(p => p[0])
          const lons = rawPoints.map(p => p[1])
          const north = Math.max(...lats),
            south = Math.min(...lats)
          const east = Math.max(...lons),
            west = Math.min(...lons)
          const latPad = Math.max((north - south) * 0.4, 0.005)
          const lonPad = Math.max((east - west) * 0.4, 0.005)
          updates.lat1 = +(north + latPad).toFixed(6)
          updates.lon1 = +(west - lonPad).toFixed(6)
          updates.lat2 = +(south - latPad).toFixed(6)
          updates.lon2 = +(east + lonPad).toFixed(6)
          const suggested = suggestZoomLevels(updates.lat1, updates.lat2, updates.lon2, updates.lon1)
          updates.zoomMin = suggested.zoomMin
          updates.zoomMax = suggested.zoomMax
        }
        setParams(prev => ({ ...prev, ...updates }))
        setCenterKey(k => k + 1)
      } catch (e) {
        console.error('[CartExport]', e)
      }
      setLoading(false)
    }
    load()
  }, [experienceUid])

  // ── Dérivés ────────────────────────────────────────────────────────────────
  const p = useMemo(
    () => ({
      lat1: parseFloat(params.lat1),
      lon1: parseFloat(params.lon1),
      lat2: parseFloat(params.lat2),
      lon2: parseFloat(params.lon2)
    }),
    [params.lat1, params.lon1, params.lat2, params.lon2]
  )

  const bboxReady = !isNaN(p.lat1) && !isNaN(p.lat2) && !isNaN(p.lon1) && !isNaN(p.lon2)
  const sourceReady = params.sourceMode === 'mapbox' || params.customUrl.trim() !== ''

  const activeBbox = useMemo(() => (bboxReady ? computeBbox(p.lat1, p.lon1, p.lat2, p.lon2) : null), [
    p.lat1,
    p.lon1,
    p.lat2,
    p.lon2,
    bboxReady
  ])

  // URL affichée dans la preview map (null = Mapbox par défaut)
  const previewCustomUrl = useMemo(() => leafletUrl(params), [params.sourceMode, params.customUrl, params.customToken])

  // Clé qui force le remontage de la carte quand le style ou le mode source change,
  // garantissant que la couche checked est bien la couche active au rendu initial.
  const mapKey = params.sourceMode === 'mapbox' ? params.style : 'custom'

  // ── Callbacks ──────────────────────────────────────────────────────────────
  const updateParam = useCallback((key, value) => {
    setParams(prev => ({ ...prev, [key]: value }))
    setEstimate(null)
    setZipBlob(null)
    // Remonte la carte (via mapKey) et restaure le cadre quand le style/mode change
    if (key === 'style' || key === 'sourceMode') {
      setCenterKey(k => k + 1)
    }
  }, [])

  const handleBboxChange = useCallback(newBbox => {
    setParams(prev => ({
      ...prev,
      lat1: +newBbox.north.toFixed(6),
      lon1: +newBbox.west.toFixed(6),
      lat2: +newBbox.south.toFixed(6),
      lon2: +newBbox.east.toFixed(6)
    }))
    setEstimate(null)
    setZipBlob(null)
  }, [])

  const applySuggestedZoom = () => {
    if (!activeBbox) return
    const { zoomMin, zoomMax } = suggestZoomLevels(activeBbox.north, activeBbox.south, activeBbox.east, activeBbox.west)
    setParams(prev => ({ ...prev, zoomMin, zoomMax }))
    setEstimate(null)
  }

  // ── Estimation ─────────────────────────────────────────────────────────────
  const computeEstimate = async () => {
    if (!bboxReady || !activeBbox) return
    setEstimate({ loading: true })
    const urlBuilder = buildTileUrl(params)
    const fallbackBytes = params.sourceMode === 'custom' ? 20_000 : FALLBACK_TILE_BYTES[params.style] || 20_000
    const zMin = parseInt(params.zoomMin)
    const zMax = parseInt(params.zoomMax)
    try {
      let totalBytes = 0,
        totalTiles = 0,
        totalSampled = 0
      for (let zoom = zMin; zoom <= zMax; zoom++) {
        const [x1, y1] = deg2num(zoom, activeBbox.north, activeBbox.west)
        const [x2, y2] = deg2num(zoom, activeBbox.south, activeBbox.east)
        const tilesAtZoom = (Math.abs(x2 - x1) + 1) * (Math.abs(y2 - y1) + 1)
        const avgBytes = await sampleTileBytes(urlBuilder, fallbackBytes, zoom, activeBbox, 4)
        totalBytes += tilesAtZoom * avgBytes
        totalTiles += tilesAtZoom
        totalSampled += Math.min(4, tilesAtZoom)
      }
      setEstimate({ loading: false, tiles: totalTiles, bytes: totalBytes, sampleCount: totalSampled })
    } catch {
      const tiles = countTiles(zMin, zMax, activeBbox.north, activeBbox.west, activeBbox.south, activeBbox.east)
      setEstimate({ loading: false, tiles, bytes: tiles * fallbackBytes, sampleCount: 0 })
    }
  }

  // ── Génération ─────────────────────────────────────────────────────────────
  const generate = async () => {
    if (!bboxReady || !activeBbox || !sourceReady) return

    // Annuler une génération éventuelle en cours
    if (abortCtrlRef.current) abortCtrlRef.current.abort()
    const ctrl = new AbortController()
    abortCtrlRef.current = ctrl

    const zMin = parseInt(params.zoomMin)
    const zMax = parseInt(params.zoomMax)
    const { north, south, east, west } = activeBbox

    const tileList = []
    for (let z = zMin; z <= zMax; z++) {
      const [x1, y1] = deg2num(z, north, west)
      const [x2, y2] = deg2num(z, south, east)
      for (let x = Math.min(x1, x2); x <= Math.max(x1, x2); x++)
        for (let y = Math.min(y1, y2); y <= Math.max(y1, y2); y++) tileList.push([z, x, y])
    }

    const total = tileList.length
    setStatus('running')
    setZipBlob(null)
    setError(null)
    setProgress({ done: 0, total, zoom: zMin, failed: 0, bytes: 0 })
    genStartRef.current = Date.now()

    try {
      const urlBuilder = buildTileUrl(params)
      const { zip, failed } = await downloadPool(
        tileList,
        urlBuilder,
        params.format,
        CONCURRENCY,
        ctrl.signal,
        (done, failed, zoom, bytes) => setProgress({ done, total, zoom, failed, bytes })
      )

      if (ctrl.signal.aborted) return

      const blob = await zip.generateAsync({ type: 'blob' })
      setZipBlob(blob)
      setStatus('done')
    } catch (err) {
      if (err.name === 'AbortError') return
      setError(err.message)
      setStatus('error')
    }
  }

  const cancel = () => {
    if (abortCtrlRef.current) abortCtrlRef.current.abort()
    setStatus('idle')
    setProgress({ done: 0, total: 0, zoom: null, failed: 0, bytes: 0 })
  }

  const download = () => {
    if (zipBlob) saveAs(zipBlob, (params.zipName || 'carto') + '.zip')
  }

  // ── Indicateurs de progression ─────────────────────────────────────────────
  const percent = progress.total > 0 ? Math.round((progress.done / progress.total) * 100) : 0

  const eta = (() => {
    if (status !== 'running' || progress.done < 1 || !genStartRef.current) return null
    const elapsed = (Date.now() - genStartRef.current) / 1000
    const remaining = (elapsed / progress.done) * (progress.total - progress.done)
    return formatEta(remaining)
  })()

  const speed = (() => {
    if (status !== 'running' || progress.done < 2 || !genStartRef.current) return null
    const elapsed = (Date.now() - genStartRef.current) / 1000
    return elapsed > 0 ? formatSpeed(progress.bytes / elapsed) : null
  })()

  const isRunning = status === 'running'

  // ── Rendu ──────────────────────────────────────────────────────────────────
  return (
    <Page>
      <NavInExperience />
      <div id="content">
        <h1>Export de cartographie</h1>
        <p className="text-muted">Génère un zip de tuiles pour une utilisation hors ligne.</p>

        {loading ? (
          <Loading />
        ) : (
          <div className="row">
            {/* ── Formulaire ─────────────────────────────────────────────── */}
            <div className="col-md-5">
              {/* Nom du zip */}
              <div className="form-group">
                <label>Nom du fichier zip</label>
                <div className="input-group">
                  <input
                    className="form-control"
                    value={params.zipName}
                    onChange={e => updateParam('zipName', e.target.value)}
                    disabled={isRunning}
                  />
                  <span className="input-group-addon">.zip</span>
                </div>
              </div>

              {/* Zoom */}
              <div className="form-group">
                <label>Niveaux de zoom</label>
                <div className="row" style={{ marginBottom: 4 }}>
                  <div className="col-xs-6">
                    <input
                      className="form-control"
                      type="number"
                      min="1"
                      max="22"
                      placeholder="min"
                      value={params.zoomMin}
                      onChange={e => updateParam('zoomMin', e.target.value)}
                      disabled={isRunning}
                    />
                  </div>
                  <div className="col-xs-6">
                    <input
                      className="form-control"
                      type="number"
                      min="1"
                      max="22"
                      placeholder="max"
                      value={params.zoomMax}
                      onChange={e => updateParam('zoomMax', e.target.value)}
                      disabled={isRunning}
                    />
                  </div>
                </div>
                {bboxReady && !isRunning && (
                  <button className="btn btn-default btn-xs" onClick={applySuggestedZoom}>
                    <i className="fa fa-magic" /> Suggérer selon la taille du cadre
                  </button>
                )}
              </div>

              {/* Cadre géographique */}
              <div className="form-group">
                <label>Cadre géographique</label>
                <p className="text-muted small" style={{ marginBottom: 6 }}>
                  Glissez les coins du cadre sur la carte pour ajuster.
                </p>
                <div className="row">
                  {[
                    ['lat1', 'Lat 1'],
                    ['lon1', 'Lon 1'],
                    ['lat2', 'Lat 2'],
                    ['lon2', 'Lon 2']
                  ].map(([key, lbl]) => (
                    <div key={key} className="col-xs-3">
                      <label className="text-muted small">{lbl}</label>
                      <input
                        className="form-control input-sm"
                        type="number"
                        step="any"
                        value={params[key]}
                        onChange={e => updateParam(key, e.target.value)}
                        disabled={isRunning}
                      />
                    </div>
                  ))}
                </div>
              </div>

              {/* ── Source de tuiles ──────────────────────────────────────── */}
              <div className="form-group">
                <label>Source de tuiles</label>
                <div style={{ display: 'flex', gap: 16, marginBottom: 10 }}>
                  {[
                    ['mapbox', 'Mapbox'],
                    ['custom', 'URL personnalisée']
                  ].map(([val, lbl]) => (
                    <label key={val} style={{ fontWeight: 400, cursor: 'pointer', margin: 0 }}>
                      <input
                        type="radio"
                        name="sourceMode"
                        value={val}
                        checked={params.sourceMode === val}
                        onChange={() => updateParam('sourceMode', val)}
                        disabled={isRunning}
                        style={{ marginRight: 5 }}
                      />
                      {lbl}
                    </label>
                  ))}
                </div>

                {params.sourceMode === 'mapbox' ? (
                  <select
                    className="form-control"
                    value={params.style}
                    onChange={e => updateParam('style', e.target.value)}
                    disabled={isRunning}
                  >
                    {MAPBOX_STYLES.map(s => (
                      <option key={s.value} value={s.value}>
                        {s.label}
                      </option>
                    ))}
                  </select>
                ) : (
                  <div>
                    <div style={{ marginBottom: 6 }}>
                      <label className="text-muted small" style={{ display: 'block', marginBottom: 2 }}>
                        Préréglages
                      </label>
                      <div style={{ display: 'flex', flexWrap: 'wrap', gap: 4 }}>
                        {TILE_PRESETS.map(preset => (
                          <button
                            key={preset.label}
                            className="btn btn-default btn-xs"
                            onClick={() => updateParam('customUrl', preset.url)}
                            disabled={isRunning}
                          >
                            {preset.label}
                          </button>
                        ))}
                      </div>
                    </div>
                    <input
                      className="form-control"
                      placeholder="https://exemple.com/{z}/{x}/{y}.png"
                      value={params.customUrl}
                      onChange={e => updateParam('customUrl', e.target.value)}
                      disabled={isRunning}
                      style={{ marginBottom: 6 }}
                    />
                    <input
                      className="form-control"
                      placeholder="Token d'accès (optionnel)"
                      value={params.customToken}
                      onChange={e => updateParam('customToken', e.target.value)}
                      disabled={isRunning}
                    />
                    <p className="text-muted small" style={{ marginTop: 4, marginBottom: 0 }}>
                      Variables supportées : <code>{'{z}'}</code> <code>{'{x}'}</code> <code>{'{y}'}</code> <code>{'{s}'}</code>
                    </p>
                  </div>
                )}
              </div>

              {/* Format */}
              <div className="form-group">
                <label>Extension dans le zip</label>
                <select
                  className="form-control"
                  value={params.format}
                  onChange={e => updateParam('format', e.target.value)}
                  disabled={isRunning}
                  style={{ width: 'auto', display: 'inline-block', marginLeft: 0 }}
                >
                  <option value="png">PNG</option>
                  <option value="jpg">JPEG</option>
                  <option value="webp">WebP</option>
                </select>
              </div>

              {/* Estimation */}
              {bboxReady && sourceReady && !isRunning && (
                <div className="form-group">
                  <button className="btn btn-default btn-sm" onClick={computeEstimate} disabled={estimate && estimate.loading}>
                    {estimate && estimate.loading ? (
                      <span>
                        <i className="fa fa-spinner fa-spin" /> Mesure en cours…
                      </span>
                    ) : (
                      <span>
                        <i className="fa fa-calculator" /> Estimer
                      </span>
                    )}
                  </button>
                  {estimate && !estimate.loading && (
                    <span style={{ marginLeft: 10 }}>
                      <strong>{estimate.tiles.toLocaleString()}</strong> tuiles · <strong>{formatBytes(estimate.bytes)}</strong>
                      {estimate.sampleCount > 0 && (
                        <span className="text-muted" style={{ marginLeft: 6, fontSize: 11 }}>
                          (mesuré sur {estimate.sampleCount} tuile{estimate.sampleCount > 1 ? 's' : ''})
                        </span>
                      )}
                      {estimate.tiles > 5000 && (
                        <span className="text-warning" style={{ marginLeft: 8 }}>
                          <i className="fa fa-warning" /> Génération longue
                        </span>
                      )}
                    </span>
                  )}
                </div>
              )}

              {/* Bouton Générer */}
              {!isRunning && (
                <button className="btn btn-primary" onClick={generate} disabled={!bboxReady || !sourceReady}>
                  <i className="fa fa-cog" /> Générer
                  {estimate && !estimate.loading && ` (${formatBytes(estimate.bytes)})`}
                </button>
              )}

              {/* Progression */}
              {isRunning && (
                <div>
                  <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 4 }}>
                    <span>
                      Zoom {progress.zoom} — <strong>{progress.done.toLocaleString()}</strong> / {progress.total.toLocaleString()}
                      {speed && (
                        <span className="text-muted" style={{ marginLeft: 8, fontSize: 12 }}>
                          {speed}
                        </span>
                      )}
                    </span>
                    <span className="text-muted">{eta && `reste ${eta}`}</span>
                  </div>
                  <div className="progress" style={{ marginBottom: 4 }}>
                    <div
                      className="progress-bar progress-bar-striped active"
                      role="progressbar"
                      style={{ width: `${percent}%`, minWidth: 28 }}
                    >
                      {percent}%
                    </div>
                  </div>
                  {progress.failed > 0 && (
                    <p style={{ margin: '4px 0 0', color: '#c0392b', fontWeight: 600 }}>
                      <i className="fa fa-exclamation-triangle" /> {progress.failed} tuile{progress.failed > 1 ? 's' : ''} en
                      échec
                    </p>
                  )}
                  <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginTop: 6 }}>
                    <p className="text-muted small" style={{ margin: 0 }}>
                      Ne fermez pas cet onglet.
                    </p>
                    <button className="btn btn-default btn-xs" onClick={cancel}>
                      <i className="fa fa-stop" /> Annuler
                    </button>
                  </div>
                </div>
              )}

              {/* Erreur */}
              {status === 'error' && (
                <div className="alert alert-danger" style={{ marginTop: 12 }}>
                  <strong>Erreur :</strong> {error}
                  <br />
                  <button className="btn btn-sm btn-default" style={{ marginTop: 8 }} onClick={generate}>
                    Réessayer
                  </button>
                </div>
              )}

              {/* Résultat */}
              {status === 'done' && zipBlob && (
                <div style={{ marginTop: 12 }}>
                  {progress.failed === 0 ? (
                    <div className="alert alert-success" style={{ marginBottom: 8 }}>
                      <i className="fa fa-check" /> {progress.total.toLocaleString()} tuiles générées avec succès.
                    </div>
                  ) : (
                    <div className="alert alert-warning" style={{ marginBottom: 8 }}>
                      <strong>
                        <i className="fa fa-exclamation-triangle" /> {progress.failed.toLocaleString()} tuile
                        {progress.failed > 1 ? 's' : ''} non téléchargée{progress.failed > 1 ? 's' : ''}
                      </strong>{' '}
                      sur {progress.total.toLocaleString()} ({Math.round((progress.failed / progress.total) * 100)} %).
                      <br />
                      <small>
                        Le zip contient les {(progress.total - progress.failed).toLocaleString()} tuiles restantes. Vous pouvez
                        relancer pour retenter les manquantes.
                      </small>
                    </div>
                  )}
                  <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
                    <button className="btn btn-success btn-lg" onClick={download}>
                      <i className="fa fa-download" /> Télécharger {params.zipName || 'carto'}.zip
                    </button>
                    {progress.failed > 0 && (
                      <button className="btn btn-default btn-lg" onClick={generate}>
                        <i className="fa fa-refresh" /> Relancer
                      </button>
                    )}
                  </div>
                </div>
              )}
            </div>

            {/* ── Carte ──────────────────────────────────────────────────── */}
            <div className="col-md-7">
              <div style={{ position: 'sticky', top: 16 }}>
                <div style={{ display: 'flex', alignItems: 'baseline', marginBottom: 8 }}>
                  <h4 style={{ margin: 0 }}>Aperçu</h4>
                  {mapPoints.length > 0 && (
                    <small className="text-muted" style={{ marginLeft: 8 }}>
                      {mapPoints.length} point{mapPoints.length > 1 ? 's' : ''}
                    </small>
                  )}
                </div>
                <PreviewMap
                  key={mapKey}
                  points={mapPoints}
                  bbox={activeBbox}
                  centerKey={centerKey}
                  onBboxChange={handleBboxChange}
                  customLayerUrl={previewCustomUrl}
                  selectedStyle={params.style}
                />
                <p className="text-muted small" style={{ marginTop: 6 }}>
                  <span
                    style={{
                      display: 'inline-block',
                      width: 10,
                      height: 10,
                      background: 'rgba(230,115,0,0.25)',
                      border: '2px solid orange',
                      marginRight: 4
                    }}
                  />
                  Cadre — glissez les coins pour redimensionner
                  <span
                    style={{
                      display: 'inline-block',
                      width: 10,
                      height: 10,
                      borderRadius: '50%',
                      background: '#e74c3c',
                      marginLeft: 12,
                      marginRight: 4
                    }}
                  />
                  Points du jeu
                </p>
              </div>
            </div>
          </div>
        )}
      </div>
      <div id="app-preview" />
    </Page>
  )
}
